import React, {useCallback, useEffect, useMemo, useState} from 'react';
import AltimeterComparison from '../../../ResultPanel/Plot/AltimeterComparison';
import {DownloadInfoItem, IBestDataset,} from '../../../../../types/analytics';
import {IEnrichedMetadata} from '../../../../../types/metadata';
import {IAdvancedAnalyticsApiPayload} from '../../../../../types/chart';
import {Spin} from 'antd';
import {UserGroupPermissions} from '../../../../../types/userGroupPermissions';
import {datasetType, GLOBAL_HD_DTU_ID} from '../../../../../utils/common';
import useDatasetStore from "../../../../../store/datasetStore";
import {usePointStore} from "../../../../../store/pointStore";
import useHomeStore from "../../../../../store/homeStore";
import useAnalyticsStore from "../../../../../store/analyticsStore";
import {ValidationTabsEnum} from "../../../../../types/validation";
import useGetMetadataQuery from "../../../../../queries/useGetMetadataQuery";
import useCreateFullExtractionTasksMutation from "../../../../../queries/useCreateFullExtractionTasksMutation";
import {useAdvancedAnalytics} from "../../../Analytics/hooks/useAdvanceAnalytics";

export default function SatelliteTab(props: {
  downloadItems: DownloadInfoItem[];
  activePointId: string;
  coordinates: number[];
  previewDatasetId: string;
  userGroupPermissions: UserGroupPermissions;
  pointName: string;
  datasetKey: string
  gridCellSize: string;
  waterDepth: string;
  activePanel: string
  activePlot: string[];
  setActivePlot: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const enrichedMetadata = useAnalyticsStore(state => state.enrichedMetadata)
  const setEnrichedMetadata = useAnalyticsStore(state => state.setEnrichedMetadata)
  const setIsChangeDatasetWarningVisible = useHomeStore(state => state.setIsChangeDatasetWarningVisible)
  const setTemporaryDatasetName = useHomeStore(state => state.setTemporaryDatasetName)
  const timeStepInterval = useHomeStore(state => state.timeStepInterval)
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs)
  const fwePoints = usePointStore(state => state.fwePoints)
  const [extractionError] = useState('');

  const {data: metadataData, isLoading: isMetadataLoading} = useGetMetadataQuery(
    props.previewDatasetId,
    props.previewDatasetId !== '' && props.downloadItems?.length > 0 && props.activePanel === ValidationTabsEnum.SATELLITE_TAB
  );

  const createFullExtractionTasksMutation = useCreateFullExtractionTasksMutation();
  const { updateAltimeterComparison, isUpdating: isAdvancedAnalyticsUpdating, images, errors } = useAdvancedAnalytics(props.coordinates);

  const onDatasetChange = (value: string) => {
    setTemporaryDatasetName(value)
    setIsChangeDatasetWarningVisible(true)
  }

  const isFWE = useMemo(() => {
    const findFwe = datasetBriefs?.find(
      (item) => props.previewDatasetId === item.Id && item.IsFWE
    );

    return !!findFwe;
  }, [props.previewDatasetId, datasetBriefs]);

  const fwePointName = useMemo(() => {
    const findFwePoint = fwePoints?.find(
      (item) => {
        return (
          props.coordinates?.[0] === Number(item.position[0].toFixed(6))
          && props.coordinates?.[1] === Number(item.position[1].toFixed(6))
        )
      }
    )
    return findFwePoint?.name || ''
  }, [props.coordinates, fwePoints])

  useEffect(() => {
    if (!metadataData || !props.previewDatasetId || props.activePanel !== ValidationTabsEnum.SATELLITE_TAB) return;

    triggerFullExtraction(props.previewDatasetId, props.coordinates.join());
  }, [metadataData, props.previewDatasetId, props.coordinates, props.activePanel]);

  async function triggerFullExtraction(
    datasetId: string,
    coordinatesString: string
  ) {
    const datasetTask = enrichedMetadata.find((task: IEnrichedMetadata) => task.DatasetId === datasetId && task.CoordinatesString === coordinatesString);

    if (datasetTask) return;
    if (!metadataData) return;

    try {
      const createFullExtractionTasks = await createFullExtractionTasksMutation.mutateAsync({
        metadatas: [metadataData],
        coordinates: props.coordinates,
        pointName: props.pointName,
        gridCellSize: props.gridCellSize,
        waterDepth: props.waterDepth,
        datasetBriefs: datasetBriefs,
        fwePoints: fwePoints,
        timeStepInterval: props.previewDatasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : undefined
      });

      const enrichedMDs = enrichedMetadata.concat(createFullExtractionTasks);

      setEnrichedMetadata(enrichedMDs);
    } catch (error) {
      console.error('Error creating full extraction tasks:', error);
    }
  }

  const onUpdateAltimeterComparison = useCallback(async (
    payload: IAdvancedAnalyticsApiPayload
  ) => {

    try {
      await updateAltimeterComparison({
        ...payload,
        PointName: isFWE ? fwePointName : payload.PointName,
        IsFwe: isFWE,
        PlotFlag: '[1 3]',
      });
    } catch (error) {
      console.error('Error getting advanced analytics data:', error);
    }
  }, [isFWE, fwePointName, updateAltimeterComparison]);

  const datasets = useMemo((): IBestDataset[] => {
    return props.downloadItems.flatMap(element => element.options.map(item => ({
      id: item.id,
      label: datasetType[element.radioKey],
      value: item.value
    })));
  }, [props.downloadItems]);

  const isLoading = isMetadataLoading ||
    createFullExtractionTasksMutation.isPending ||
    isAdvancedAnalyticsUpdating;

  if (isLoading) return (
    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
      <Spin size="large"/>
    </div>
  )

  if (extractionError) return (<div>{extractionError}</div>);

  if (props.activePlot.includes('Altimeter Comparison') && enrichedMetadata) return (
    <div style={{height: '100%'}}>
      <AltimeterComparison
        altimeterComparisonImage={images.altimeter}
        coordinates={props.coordinates}
        datasets={datasets}
        enrichedMetadata={enrichedMetadata}
        error={errors.altimeter}
        onUpdate={onUpdateAltimeterComparison}
        previewDatasetId={props.previewDatasetId}
        previewDatasetTimeRange={props.userGroupPermissions?.validationDateRange}
        pointName={props.pointName}
        activePanel={props.activePanel}
        datasetChange={onDatasetChange}
      />
    </div>
  )

  return (<div style={{height: '100%'}}></div>);
}
